import React, { useCallback, useEffect, useState } from "react"
import Box from "../../../components/Ui/Box"
import { DisabledTab, TabText } from "./ManageOrder.styled"
import { useQuery } from "@apollo/client"
import GET_ORDER from "../../../graphql/queries/order/GetOrder"
import { useNavigate, useParams } from "react-router-dom"

import * as graphql from "lib/types/generated/graphql-types"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import PageHeader from "../../../components/Ui/Page/PageHeader"
import PrimaryButton from "../../../components/Ui/Buttons/PrimaryButton"
import CancelOrderPopup from "../../../components/Order/ManageOrder/CancelOrder/CancelOrderPopup"
import RefundOrder from "../../../components/Order/ManageOrder/RefundOrder/RefundOrder"
import DeliveryOrder from "../../../components/Order/ManageOrder/DeliverOrder/DeliveryOrder"
import CompensationOrder from "../../../components/Order/ManageOrder/CompensationOrder/CompensationOrder"
import ReleaseOrder from "../../../components/Order/ManageOrder/ReleaseOrder/ReleaseOrder"
import ModifyOrderLines from "../../../components/Order/ManageOrder/ModifyOrder/ModifyOrderLines/ModifyOrderLines"
import { Compensations } from "../../../classes/order/Compensations"
import { Deliveries } from "../../../classes/order/Deliveries"
import { ModificationOrderLines } from "../../../classes/order/ModificationOrderLines"
import { Refunds } from "../../../classes/order/Refunds"
import { Releases } from "../../../classes/order/Releases"
import { Cancellations } from "../../../classes/order/Cancellations"
import CancelOrder from "../../../components/Order/ManageOrder/CancelOrder/CancelOrder"
import ModifyShippingFees from "../../../components/Order/ManageOrder/ModifyOrder/ModifyShippingFees/ModifyShippingFees"
import { ModificationShippingFees } from "../../../classes/order/ModificationShippingFees"
import ALL_REASON_CODES from "../../../graphql/queries/settings/reasonCodes/AllReasonCodes"
import ALL_CUSTOM_STATES from "../../../graphql/queries/settings/customState/AllCustomStates"
import BlockedOperationsBanner from "../../../components/Order/ManageOrder/BlockedOperationsBanner"
import BlockedTabPanel from "../../../components/Order/ManageOrder/BlockedTabPanel"
import { ReactComponent as BanIcon } from "images/icons/ban.svg"
import { getBlockedOperations } from "../../../helpers/getBlockedOperations"

const ManageOrder = () => {
  const { id } = useParams()
  const { data, refetch, loading } = useQuery(GET_ORDER, { variables: { id } })
  const [showCancelPopup, setShowCancelPopup] = useState(false)
  const navigate = useNavigate()

  const order = data?.Order?.details
  const orderStatus = order?.status as graphql.OrderStatus
  const orderHistory = data?.Order?.history as graphql.OrderHistory
  const modificationOrderLines = new ModificationOrderLines(order?.Modifications)
  const modificationShippingFees = new ModificationShippingFees(order?.Modifications)
  const deliveries = new Deliveries(order?.Deliveries)
  const releases = new Releases(order?.Releases)
  const refunds = new Refunds(order?.Refunds)
  const compensations = new Compensations(order?.Compensations)
  const cancellations = new Cancellations(order?.Cancellations)

  const showDelivery = () =>
    !releases.completed() &&
    !cancellations.active() &&
    !modificationOrderLines.active() &&
    !modificationShippingFees.active() &&
    !releases.active() &&
    !refunds.active() &&
    !compensations.active() &&
    !deliveries.isOrderFullyDelivered(order)

  const showRelease = () =>
    !releases.completed() &&
    !orderStatus?.orderStates?.includes(graphql.OrderState.FullyDelivered) &&
    deliveries.completed() &&
    !deliveries.active() &&
    !refunds.pending() &&
    !refunds.ongoing() &&
    !refunds.failed() &&
    !compensations.pending() &&
    !compensations.ongoing() &&
    !compensations.failed()

  const showCompensation = () =>
    deliveries.completed() &&
    !deliveries.active() &&
    !releases.pending() &&
    !releases.ongoing() &&
    !releases.failed() &&
    !refunds.ongoing() &&
    !refunds.pending() &&
    !refunds.failed()
  const showRefund = () =>
    deliveries.completed() && !deliveries.active() && !releases.active() && !compensations.active()
  const showModifyOrderLines = () =>
    order &&
    !deliveries.completed() &&
    !cancellations.active() &&
    !deliveries.active() &&
    !modificationShippingFees.active()
  const showModifyShippingFees = () =>
    order &&
    order.shippingFees.length > 0 &&
    !deliveries.completed() &&
    !cancellations.active() &&
    !deliveries.active() &&
    !modificationOrderLines.active()

  const showCancellation = () =>
    !deliveries.completed() &&
    !deliveries.active() &&
    !modificationOrderLines.active() &&
    !modificationShippingFees.active() &&
    (cancellations.failed() || cancellations.ongoing() || cancellations.pending())

  const showCancellationButton = () =>
    !deliveries.completed() &&
    !deliveries.active() &&
    !modificationOrderLines.active() &&
    !modificationShippingFees.active() &&
    !cancellations.active()

  useEffect(() => {
    if (cancellations.completed()) {
      navigate(`/orders/${id}`)
    }
  }, [order?.Cancellations])

  const { data: reasonData } = useQuery(ALL_REASON_CODES)
  const customReasons = reasonData?.Order?.customReasonCodes?.codes

  const { data: customStateData } = useQuery(ALL_CUSTOM_STATES)
  const allCustomStates: graphql.CustomState[] = customStateData?.Order?.customStates?.states ?? []
  const orderCustomStates: graphql.OrderCustomState[] = orderStatus?.customStates ?? []

  const blockedOperations = getBlockedOperations(orderCustomStates)

  const isBlocked = (operation: graphql.CustomOperation) => blockedOperations.has(operation)

  const getCustomState = useCallback(
    (stateId: string) => allCustomStates.find((s) => s.id === stateId),
    [allCustomStates]
  )

  const getBlockingStateDescription = (operation: graphql.CustomOperation): string | undefined => {
    const blockingState = orderCustomStates.find(
      (s) => s.type === graphql.CustomStateType.Blocking && s.operations?.includes(operation)
    )
    if (!blockingState) return undefined
    return getCustomState(blockingState.id)?.description ?? blockingState.id
  }

  type VisibleTab = { operation: graphql.CustomOperation; blocked: boolean }
  const visibleTabs: VisibleTab[] = [
    ...(showDelivery()
      ? [
          {
            operation: graphql.CustomOperation.Delivery,
            blocked: isBlocked(graphql.CustomOperation.Delivery)
          }
        ]
      : []),
    ...(showModifyOrderLines()
      ? [
          {
            operation: graphql.CustomOperation.ModifyOrderLines,
            blocked: isBlocked(graphql.CustomOperation.ModifyOrderLines)
          }
        ]
      : []),
    ...(showModifyShippingFees()
      ? [
          {
            operation: graphql.CustomOperation.ModifyShippingFees,
            blocked: isBlocked(graphql.CustomOperation.ModifyShippingFees)
          }
        ]
      : []),
    ...(deliveries.completed()
      ? [
          ...(showRelease()
            ? [
                {
                  operation: graphql.CustomOperation.Release,
                  blocked: isBlocked(graphql.CustomOperation.Release)
                }
              ]
            : []),
          ...(showRefund()
            ? [
                {
                  operation: graphql.CustomOperation.Refund,
                  blocked: isBlocked(graphql.CustomOperation.Refund)
                }
              ]
            : []),
          ...(showCompensation()
            ? [
                {
                  operation: graphql.CustomOperation.Compensation,
                  blocked: isBlocked(graphql.CustomOperation.Compensation)
                }
              ]
            : [])
        ]
      : []),
    ...(showCancellation()
      ? [{ operation: graphql.CustomOperation.Cancellation, blocked: false }]
      : [])
  ]

  const defaultIndex = Math.max(
    visibleTabs.findIndex((t) => !t.blocked),
    0
  )

  const renderTab = (operation: graphql.CustomOperation, label: string) => (
    <Tab>
      {isBlocked(operation) ? (
        <DisabledTab>
          <BanIcon /> {label}
        </DisabledTab>
      ) : (
        label
      )}
    </Tab>
  )

  const renderBlockedOrContent = (operation: graphql.CustomOperation, content: React.ReactNode) =>
    isBlocked(operation) ? (
      <BlockedTabPanel
        operation={operation}
        stateDescription={getBlockingStateDescription(operation)}
      />
    ) : (
      content
    )

  return (
    <Box padding="0" margin="0">
      <PageHeader
        title={"Manage order #" + (order?.reference ?? "")}
        description={<>Manage order</>}
        goBackLinkUrl={`/orders/${id}`}
        goBackLinkText="Back to order"
        hasTabs
      >
        {showCancellationButton() &&
          (isBlocked(graphql.CustomOperation.Cancellation) ? (
            <PrimaryButton mode="delete" disabled>
              <BanIcon /> Cancel order
            </PrimaryButton>
          ) : (
            <PrimaryButton mode="delete" handleClick={() => setShowCancelPopup(true)}>
              Cancel order
            </PrimaryButton>
          ))}
      </PageHeader>
      <BlockedOperationsBanner
        orderCustomStates={orderCustomStates}
        getCustomState={getCustomState}
      />
      <CancelOrderPopup
        setShowCancelPopup={setShowCancelPopup}
        showCancelPopup={showCancelPopup}
        order={order}
        refetch={refetch}
        refetchLoading={loading}
      />
      <div className={loading ? "blur" : ""}>
        <Tabs defaultIndex={defaultIndex}>
          <TabList>
            {showDelivery() && renderTab(graphql.CustomOperation.Delivery, "Deliveries")}
            {showModifyOrderLines() &&
              renderTab(graphql.CustomOperation.ModifyOrderLines, "Modify order lines")}
            {showModifyShippingFees() &&
              renderTab(graphql.CustomOperation.ModifyShippingFees, "Modify shipping fees")}
            {deliveries.completed() && (
              <>
                {showRelease() && renderTab(graphql.CustomOperation.Release, "Releases")}
                {showRefund() && renderTab(graphql.CustomOperation.Refund, "Refunds")}
                {showCompensation() &&
                  renderTab(graphql.CustomOperation.Compensation, "Compensations")}
              </>
            )}
            {showCancellation() && (
              <Tab>
                <TabText>Cancellations</TabText>
              </Tab>
            )}
          </TabList>
          {showDelivery() && (
            <TabPanel>
              {renderBlockedOrContent(
                graphql.CustomOperation.Delivery,
                <DeliveryOrder
                  order={order}
                  refetch={refetch}
                  loading={loading}
                  orderHistory={orderHistory}
                  customReasons={customReasons}
                />
              )}
            </TabPanel>
          )}
          {showModifyOrderLines() && (
            <TabPanel>
              {renderBlockedOrContent(
                graphql.CustomOperation.ModifyOrderLines,
                <ModifyOrderLines
                  order={order}
                  refetch={refetch}
                  loading={loading}
                  orderHistory={orderHistory}
                  customReasons={customReasons}
                />
              )}
            </TabPanel>
          )}
          {showModifyShippingFees() && (
            <TabPanel>
              {renderBlockedOrContent(
                graphql.CustomOperation.ModifyShippingFees,
                <ModifyShippingFees
                  order={order}
                  refetch={refetch}
                  loading={loading}
                  orderHistory={orderHistory}
                  customReasons={customReasons}
                />
              )}
            </TabPanel>
          )}
          {showRelease() && (
            <TabPanel>
              {renderBlockedOrContent(
                graphql.CustomOperation.Release,
                <ReleaseOrder
                  order={order}
                  refetch={refetch}
                  loading={loading}
                  failedReleases={releases.failedReleases()}
                  orderHistory={orderHistory}
                />
              )}
            </TabPanel>
          )}
          {showRefund() && (
            <TabPanel>
              {renderBlockedOrContent(
                graphql.CustomOperation.Refund,
                <RefundOrder
                  order={order}
                  refetch={refetch}
                  failedRefunds={refunds.failedRefunds()}
                  loading={loading}
                  orderHistory={orderHistory}
                  customReasons={customReasons}
                />
              )}
            </TabPanel>
          )}
          {showCompensation() && (
            <TabPanel>
              {renderBlockedOrContent(
                graphql.CustomOperation.Compensation,
                <CompensationOrder
                  order={order}
                  refetch={refetch}
                  failedCompensations={compensations.failedCompensations()}
                  loading={loading}
                  orderHistory={orderHistory}
                  customReasons={customReasons}
                />
              )}
            </TabPanel>
          )}
          {showCancellation() && (
            <TabPanel>
              <CancelOrder
                order={order}
                refetch={refetch}
                orderHistory={orderHistory}
                setShowCancelPopup={setShowCancelPopup}
              />
            </TabPanel>
          )}
        </Tabs>
      </div>
    </Box>
  )
}

export default ManageOrder
